import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectProps } from 'antd/es/select';
import { fetchApi } from 'api/fetchApi';
import { debounce } from 'lodash-es';
import { handleError } from 'utils/errors';
import { ISelectOption } from './types';

export interface AsyncSelectProps<ValueType = any> extends Omit<SelectProps<ValueType>, 'options' | 'children'> {
  name: string;
  required: boolean;
  take: number;
  callbackUrl: string;
  searchUrl: string;
  amountOfSymbolsForSearchStart: number;
  debounceTimeout?: number;
  onSetChange: (
    name: string,
    value: Array<string> | Record<string, string> | ISelectOption,
    callbackUrl: string,
  ) => void;
}

const MAX_LENGTH = 64;

export const AsyncSelect = ({
  name,
  required,
  take,
  callbackUrl,
  searchUrl,
  debounceTimeout = 800,
  amountOfSymbolsForSearchStart,
  onSetChange,
  ...props
}: AsyncSelectProps) => {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = async (value: string) => {
      if (value.length < amountOfSymbolsForSearchStart) return;

      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      setOptions([]);
      setFetching(true);

      try {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }

        const { data } = await fetchApi.get<ISelectOption[]>(`${searchUrl}?search=${value}&take=${take}&skip=0`);

        setOptions(data);
        setFetching(false);
      } catch (error) {
        handleError('Failed to fetch options', error);
      }
    };

    return debounce(loadOptions, debounceTimeout);
  }, [callbackUrl, debounceTimeout]);

  const handleSelect = useCallback((option: ISelectOption) => {
    onSetChange(name, option, callbackUrl);
  }, []);

  return (
    <Select
      className="asyncSelect"
      popupClassName="asyncSelect__dropdown"
      data-test-id={name}
      labelInValue
      aria-required={required}
      maxLength={MAX_LENGTH}
      showSearch
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
      onSelect={handleSelect}
    />
  );
};
